import { useEffect, useState } from "react";
import { UserPlus, Trash2, KeyRound, Inbox, RefreshCw, Loader2, Copy, Users, Shield, GitBranch, Activity, AlertTriangle, ToggleLeft, ToggleRight, Pencil, Bell, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageShell } from "@/components/Layout";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

function CreateUserDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: "", display_name: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // {username, temp_password, display_name}
  const [copied, setCopied] = useState(false);

  const genPass = () => setForm((f) => ({ ...f, password: Math.random().toString(36).slice(2, 8) + "!" + Math.floor(Math.random() * 90 + 10) }));

  const loginUrl = "https://nflpickhouse.com/login";
  const welcomeText = created
    ? `🏈 You're in! Welcome to NFL PickHouse.

LOGIN
Site: ${loginUrl}
Username: ${created.username}
Temporary Password: ${created.temp_password}

IMPORTANT:
Save this password now. It is only shown once.
You can change it later under Password if you want.

HOW PICKHOUSE WORKS
Each NFL game has 3 picks:
• Game Winner
• Team with the Most Passing Yards
• Team with the Most Rushing Yards
Make your selections under MAKE PICKS before that game's kickoff.

⚔️ PICKHOUSE SHOWDOWN
Each week you are matched head-to-head against another player.
Every correct:
• Winner pick = 1 Showdown point
• Passing pick = 1 Showdown point
• Rushing pick = 1 Showdown point
The player with the most Showdown points wins the weekly matchup.

🚀 HAIL MARY
During the Regular Season you also receive 3 Hail Mary cards for the season.
A Hail Mary lets you choose one NFL game, select one of the QBs playing in that game, and predict his:
• Passing-yard range
• Passing-TD range
Hail Mary cards are limited, so use them strategically.
Hail Mary is not active during Preseason.

🏆 PLAYOFFS
Showdown standings run through the Regular Season and determine the PickHouse playoff field.
Your Showdown page shows your matchup, schedule, standings and scores.

If you have any problems logging in, making picks, or using the site, contact your Commissioner:
Mike Mawson
mikemawson203@gmail.com

Have fun — and make sure your picks are in before kickoff. 🏈`
    : "";

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", form);
      setCreated({ username: data.username, temp_password: data.temp_password, display_name: data.display_name });
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copyAll = async () => {
    const htmlText = welcomeText
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .split(loginUrl).join(`<a href="${loginUrl}">${loginUrl}</a>`)
      .replace(/\n/g, "<br>");
    try {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([htmlText], { type: "text/html" }),
            "text/plain": new Blob([welcomeText], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(welcomeText);
      }
      setCopied(true);
      toast.success("Welcome message copied — the login link stays clickable when pasted");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      try {
        await navigator.clipboard.writeText(welcomeText);
        setCopied(true);
        toast.success("Welcome message copied to clipboard");
        setTimeout(() => setCopied(false), 2500);
      } catch {
        toast.error("Couldn't copy — select the text and copy manually");
      }
    }
  };

  const reset = () => {
    setForm({ username: "", display_name: "", password: "" });
    setCreated(null);
    setCopied(false);
  };

  const handleOpenChange = (o) => {
    setOpen(o);
    if (!o) reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button data-testid="open-create-user-btn"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#D50A0A] text-white font-bold hover:shadow-[0_0_18px_rgba(213,10,10,0.4)] active:scale-95 transition-transform">
          <UserPlus className="h-4 w-4" /> Add Player
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border-[#1F1F22] text-white">
        {!created ? (
          <>
            <DialogHeader><DialogTitle className="font-display">Provision New Player</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-xs text-zinc-400 font-semibold">Username</label>
                <input data-testid="new-user-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                  className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#D50A0A]" placeholder="jsmith" />
              </div>
              <div>
                <label className="text-xs text-zinc-400 font-semibold">Display name</label>
                <input data-testid="new-user-display" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                  className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#D50A0A]" placeholder="John Smith" />
              </div>
              <div>
                <label className="text-xs text-zinc-400 font-semibold">Temporary password</label>
                <div className="flex gap-2 mt-1">
                  <input data-testid="new-user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="flex-1 rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#D50A0A]" placeholder="temp pass" />
                  <button onClick={genPass} className="px-3 rounded-lg border border-[#1F1F22] text-xs text-zinc-300 hover:border-[#D50A0A]">Generate</button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <button data-testid="submit-create-user-btn" onClick={submit} disabled={busy || !form.username || !form.password}
                className="w-full py-3 rounded-full bg-[#D50A0A] text-white font-bold active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create Account
              </button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Account created</DialogTitle>
              <p className="text-sm text-zinc-400 mt-1">{created.display_name}</p>
            </DialogHeader>
            <div className="space-y-4 py-1">
              <p className="text-xs text-amber-400/90">Save these login details now — the temporary password is shown only once.</p>

              {/* LOGIN DETAILS */}
              <div data-testid="login-details" className="rounded-xl border border-[#D50A0A]/40 bg-[#D50A0A]/5 p-4 space-y-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#ff7a7a]">Login Details</div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-zinc-500">Site</div>
                  <a data-testid="welcome-login-link" href={loginUrl} target="_blank" rel="noopener noreferrer"
                    className="text-sm text-[#4d90ff] font-semibold underline underline-offset-2 hover:text-[#7ab0ff] break-all">{loginUrl}</a>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-zinc-500">Username</div>
                  <div data-testid="created-username" className="font-mono text-base font-bold text-white select-all break-all">{created.username}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-zinc-500">Temporary Password</div>
                  <div data-testid="created-password" className="font-mono text-base font-bold text-white select-all break-all">{created.temp_password}</div>
                </div>
              </div>

              {/* HOW-TO SUMMARY */}
              <div className="rounded-xl border border-[#1F1F22] bg-[#121212] p-4 space-y-3 max-h-56 overflow-auto text-xs leading-relaxed">
                <div>
                  <div className="font-display text-[11px] font-bold uppercase tracking-wide text-zinc-200">How to Play</div>
                  <p className="text-zinc-400 mt-1">Each game has 3 picks — Game Winner, Most Passing Yards, Most Rushing Yards. Lock them in under <span className="text-zinc-200 font-semibold">Make Picks</span> before kickoff.</p>
                </div>
                <div>
                  <div className="font-display text-[11px] font-bold uppercase tracking-wide text-zinc-200">Showdown</div>
                  <p className="text-zinc-400 mt-1">You're matched head-to-head weekly. Each correct pick = 1 Showdown point. Most points wins the matchup.</p>
                </div>
                <div>
                  <div className="font-display text-[11px] font-bold uppercase tracking-wide text-zinc-200">Hail Mary</div>
                  <p className="text-zinc-400 mt-1">3 cards for the Regular Season. Pick a game & QB, predict his passing-yard and passing-TD ranges. Not active in Preseason.</p>
                </div>
                <div>
                  <div className="font-display text-[11px] font-bold uppercase tracking-wide text-zinc-200">Need Help?</div>
                  <p className="text-zinc-400 mt-1">Contact your Commissioner: Mike Mawson · <span className="text-zinc-200">mikemawson203@gmail.com</span></p>
                </div>
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <button data-testid="copy-welcome-btn" onClick={copyAll}
                className="w-full py-3 rounded-full bg-[#D50A0A] text-white font-bold active:scale-95 transition-transform flex items-center justify-center gap-2">
                <Copy className="h-4 w-4" /> {copied ? "Copied!" : "Copy Welcome Message"}
              </button>
              <button data-testid="create-another-btn" onClick={reset}
                className="w-full py-3 rounded-full border border-[#1F1F22] text-sm font-semibold text-zinc-300 hover:border-[#D50A0A] hover:text-white transition-colors">
                Add Another Player
              </button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ username: "", display_name: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) setForm({ username: user.username || "", display_name: user.display_name || "", password: "" });
  }, [user]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        username: form.username,
        display_name: form.display_name,
      };
      if (form.password) payload.password = form.password;
      await api.patch(`/admin/users/${user.id}`, payload);
      toast.success(form.password ? `Password reset successfully for ${form.username}.` : `Updated ${form.username}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0A0A0A] border-[#1F1F22] text-white" data-testid="edit-user-dialog">
        <DialogHeader><DialogTitle className="font-display">Edit Player</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs text-zinc-400 font-semibold">Username (login)</label>
            <input data-testid="edit-user-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#D50A0A]" placeholder="jsmith" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 font-semibold">Display name</label>
            <input data-testid="edit-user-display" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#D50A0A]" placeholder="John Smith" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 font-semibold">New password <span className="text-zinc-600 font-normal">(leave blank to keep current)</span></label>
            <input data-testid="edit-user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#D50A0A]" placeholder="leave blank to keep" />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="submit-edit-user-btn" onClick={submit} disabled={busy || !form.username}
            className="w-full py-3 rounded-full bg-[#D50A0A] text-white font-bold active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save Changes
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose, onDone }) {
  const [stage, setStage] = useState("confirm"); // confirm | done
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {username, temp_password, display_name}
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user) { setStage("confirm"); setResult(null); setCopied(false); }
  }, [user]);

  const loginUrl = "https://nflpickhouse.com/login";
  const genPass = () => Math.random().toString(36).slice(2, 8) + "!" + Math.floor(Math.random() * 90 + 10);

  const resetText = result
    ? `🏈 NFL PickHouse Login Reset

Your PickHouse password has been reset.

Site:
${loginUrl}
Username:
${result.username}
Temporary Password:
${result.temp_password}

Save this password now — the temporary password is only shown once.
After signing in, you can change it under Password if you want.

If you have any trouble logging in, contact:
Mike Mawson
mikemawson203@gmail.com`
    : "";

  const confirmReset = async () => {
    setBusy(true);
    const newPass = genPass();
    try {
      await api.patch(`/admin/users/${user.id}`, { password: newPass });
      setResult({ username: user.username, temp_password: newPass, display_name: user.display_name });
      setStage("done");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copyReset = async () => {
    const htmlText = resetText
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .split(loginUrl).join(`<a href="${loginUrl}">${loginUrl}</a>`)
      .replace(/\n/g, "<br>");
    try {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([htmlText], { type: "text/html" }),
            "text/plain": new Blob([resetText], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(resetText);
      }
      setCopied(true);
      toast.success("Reset message copied — the login link stays clickable when pasted");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      try {
        await navigator.clipboard.writeText(resetText);
        setCopied(true);
        toast.success("Reset message copied to clipboard");
        setTimeout(() => setCopied(false), 2500);
      } catch {
        toast.error("Couldn't copy — select the text and copy manually");
      }
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0A0A0A] border-[#1F1F22] text-white" data-testid="reset-pw-dialog">
        {stage === "confirm" ? (
          <>
            <DialogHeader><DialogTitle className="font-display">Reset Player Password?</DialogTitle></DialogHeader>
            <div className="space-y-2 py-2">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">Player</div>
              <div data-testid="reset-confirm-name" className="font-display text-lg font-bold">{user?.display_name}</div>
              <p className="text-xs text-zinc-400 pt-2">This will replace their current password with a new temporary password.</p>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <button data-testid="confirm-reset-btn" onClick={confirmReset} disabled={busy}
                className="w-full py-3 rounded-full bg-[#D50A0A] text-white font-bold active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Reset Password
              </button>
              <button data-testid="cancel-reset-btn" onClick={onClose}
                className="w-full py-3 rounded-full border border-[#1F1F22] text-sm font-semibold text-zinc-300 hover:border-[#D50A0A] hover:text-white transition-colors">
                Cancel
              </button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Password Reset</DialogTitle>
              <p className="text-sm text-zinc-400 mt-1">{result?.display_name}</p>
            </DialogHeader>
            <div className="space-y-4 py-1">
              <p className="text-xs text-amber-400/90">Save these login details now — the temporary password is shown only once.</p>
              <div data-testid="reset-login-details" className="rounded-xl border border-[#D50A0A]/40 bg-[#D50A0A]/5 p-4 space-y-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#ff7a7a]">Login Details</div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-zinc-500">Site</div>
                  <a data-testid="reset-login-link" href={loginUrl} target="_blank" rel="noopener noreferrer"
                    className="text-sm text-[#4d90ff] font-semibold underline underline-offset-2 hover:text-[#7ab0ff] break-all">{loginUrl}</a>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-zinc-500">Username</div>
                  <div data-testid="reset-username" className="font-mono text-base font-bold text-white select-all break-all">{result?.username}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-zinc-500">Temporary Password</div>
                  <div data-testid="reset-password" className="font-mono text-base font-bold text-white select-all break-all">{result?.temp_password}</div>
                </div>
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <button data-testid="copy-reset-btn" onClick={copyReset}
                className="w-full py-3 rounded-full bg-[#D50A0A] text-white font-bold active:scale-95 transition-transform flex items-center justify-center gap-2">
                <Copy className="h-4 w-4" /> {copied ? "Copied!" : "Copy Reset Message"}
              </button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}

function IncompletePicksDialog({ open, onClose, data, onSend }) {
  const list = data?.incomplete || [];
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0A0A0A] border-[#1F1F22] text-white max-w-lg" data-testid="incomplete-picks-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Incomplete Picks</DialogTitle>
          <p className="text-sm text-zinc-400 mt-1">{data?.week_label || "Current week"} · {list.length} player{list.length === 1 ? "" : "s"}</p>
        </DialogHeader>
        <div className="space-y-3 py-1 max-h-[60vh] overflow-auto">
          {list.length === 0 ? (
            <p className="text-sm text-zinc-500 py-8 text-center">Everyone is caught up 🎉</p>
          ) : list.map((p) => (
            <div key={p.user_id} data-testid={`incomplete-row-${p.username}`}
              className="rounded-xl border border-[#1F1F22] bg-[#121212] p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-display text-sm font-bold">{p.display_name}</div>
                <div className="text-xs text-zinc-400 mt-1">{p.completed_games} / {p.total_games} games complete</div>
                <div className="text-xs text-amber-300 mt-0.5">{p.remaining_editable} game{p.remaining_editable === 1 ? "" : "s"} still need picks</div>
                {p.missed_locked > 0 && (
                  <div className="text-xs text-zinc-500 mt-0.5">{p.missed_locked} missed/locked</div>
                )}
                {p.last_reminder_at && (
                  <div data-testid={`reminder-sent-${p.username}`} className="text-[11px] text-emerald-400/90 mt-1">Reminder sent {fmtTime(p.last_reminder_at)}</div>
                )}
              </div>
              <button data-testid={`send-reminder-${p.username}`} onClick={() => onSend(p)}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#D50A0A] text-white text-xs font-bold active:scale-95 transition-transform">
                <Send className="h-3.5 w-3.5" /> Send Reminder
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncInfo, setSyncInfo] = useState(null);
  const [apiStatus, setApiStatus] = useState(null);
  const [league, setLeague] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [showIncomplete, setShowIncomplete] = useState(false);

  const load = async () => {
    setLoading(true);
    const [u, r, s, a, l, c] = await Promise.all([
      api.get("/admin/users"),
      api.get("/admin/access-requests"),
      api.get("/season/current"),
      api.get("/admin/api-status"),
      api.get("/league"),
      api.get("/admin/pick-completeness"),
    ]);
    setUsers(u.data);
    setRequests(r.data);
    setSyncInfo(s.data.sync);
    setApiStatus(a.data);
    setLeague(l.data);
    setCompleteness(c.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { active: !u.active });
      toast.success(`${u.username} ${u.active ? "deactivated" : "activated"}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.username}? This removes their picks too.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${u.username}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const markHandled = async (r) => {
    await api.patch(`/admin/access-requests/${r.id}`);
    load();
  };

  const sendReminder = async (p) => {
    if (p.last_reminder_at && (Date.now() - new Date(p.last_reminder_at).getTime()) < 10 * 60 * 1000) {
      if (!window.confirm(`Send another reminder to ${p.display_name}?`)) return;
    }
    try {
      await api.post(`/admin/pick-reminder/${p.user_id}`);
      toast.success(`Reminder sent to ${p.display_name}`);
      const c = await api.get("/admin/pick-completeness");
      setCompleteness(c.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await api.post("/admin/sync");
      toast.success("Schedule sync started — fetching the latest NFL schedule from Tank01");
      setTimeout(() => { load(); setSyncing(false); }, 10000);
    } catch {
      toast.error("Sync failed");
      setSyncing(false);
    }
  };

  const generatePlayoffs = async () => {
    try {
      const { data } = await api.post("/admin/generate-playoffs");
      toast.success(`Playoff bracket generated (${data.games} games)`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const updateScores = async () => {
    setScoring(true);
    try {
      await api.post("/admin/update-scores");
      toast.success("Result update started — fetching final scores from Tank01");
      setTimeout(async () => {
        try {
          const { data } = await api.get("/admin/api-status");
          if (data?.last_request && data.last_request.success === false) {
            toast.error(
              data.last_request.status === 429
                ? "NFL data API rejected the request (429) — key invalid/expired or quota exhausted. Scores not updated."
                : "NFL data API request failed — scores not updated."
            );
          } else {
            toast.success("Game results refreshed from Tank01");
          }
        } catch { /* status refresh only */ }
        load();
        setScoring(false);
      }, 10000);
    } catch {
      toast.error("Score update failed");
      setScoring(false);
    }
  };

  const pendingReqs = requests.filter((r) => r.status === "pending").length;

  return (
    <PageShell testid="admin-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <span className="overline flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> Commissioner Mode</span>
          <h1 className="font-display text-4xl font-black tracking-tight mt-2">Admin Dashboard</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button data-testid="sync-btn" onClick={triggerSync} disabled={syncing}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-[#1F1F22] text-sm font-semibold hover:border-[#D50A0A] hover:text-[#D50A0A] transition-colors disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> Sync NFL Schedule
          </button>
          <button data-testid="update-scores-btn" onClick={updateScores} disabled={scoring}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-[#1F1F22] text-sm font-semibold hover:border-[#D50A0A] hover:text-[#D50A0A] transition-colors disabled:opacity-50">
            <Activity className={`h-4 w-4 ${scoring ? "animate-pulse" : ""}`} /> Update Game Results
          </button>
          <button data-testid="generate-playoffs-btn" onClick={generatePlayoffs}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-[#1F1F22] text-sm font-semibold hover:border-[#D50A0A] hover:text-[#D50A0A] transition-colors">
            <GitBranch className="h-4 w-4" /> Generate Playoffs
          </button>
          <CreateUserDialog onCreated={load} />
        </div>
      </div>

      {completeness?.incomplete_count > 0 && (
        <div data-testid="incomplete-picks-alert"
          className="rounded-xl border border-amber-500/50 bg-amber-500/10 px-5 py-4 mb-6 flex items-center gap-3 flex-wrap">
          <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0" />
          <div className="text-sm font-bold text-amber-200">
            ⚠️ {completeness.incomplete_count} PLAYER{completeness.incomplete_count === 1 ? "" : "S"} HAVE INCOMPLETE PICKS
            <span className="font-normal text-amber-200/70"> · {completeness.week_label}</span>
          </div>
          <button data-testid="view-incomplete-btn" onClick={() => setShowIncomplete(true)}
            className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-amber-500/50 text-sm font-semibold text-amber-200 hover:border-amber-400 hover:text-amber-100 transition-colors">
            <Bell className="h-4 w-4" /> View Players
          </button>
        </div>
      )}

      <IncompletePicksDialog open={showIncomplete} onClose={() => setShowIncomplete(false)}
        data={completeness} onSend={sendReminder} />

      {apiStatus?.last_request && apiStatus.last_request.success === false && (
        <div data-testid="api-error-banner" className="rounded-xl border border-red-500/50 bg-red-500/10 px-5 py-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-bold text-red-300">Live scores paused — the NFL data API is rejecting requests</div>
            <div className="text-red-200/80 mt-1">
              The last request returned an error{apiStatus.last_request.status ? ` (code ${apiStatus.last_request.status})` : ""}.
              {apiStatus.last_request.status === 429
                ? " This usually means the RapidAPI key is invalid, expired, or its quota is exhausted."
                : " The NFL data provider could not be reached."}
              {" "}Game results won't update until this is resolved. If you recently changed keys, make sure the deployed site's <span className="font-semibold">RAPIDAPI_KEY</span> environment variable is updated, then redeploy.
            </div>
          </div>
        </div>
      )}

      <div data-testid="sync-status" className="rounded-xl border border-[#1F1F22] bg-[#0A0A0A] px-4 py-3 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-xs text-zinc-400">
        <span>Schedule last synced:{" "}
          <span className="text-white font-semibold block" data-testid="last-schedule-synced">
            {syncInfo?.last_schedule_sync ? new Date(syncInfo.last_schedule_sync).toLocaleString() : "—"}
          </span>
        </span>
        <span>Results last updated:{" "}
          <span className="text-white font-semibold block" data-testid="last-score-updated">
            {syncInfo?.last_score_update ? new Date(syncInfo.last_score_update).toLocaleString() : "—"}
          </span>
        </span>
        <span>Source / games:{" "}
          <span className="text-white font-semibold block">{(syncInfo?.source || "—")} · {syncInfo?.games ?? "—"} games</span>
        </span>
        <span>Last result batch:{" "}
          <span className="text-white font-semibold block">{syncInfo?.scores_updated_last ?? 0} games updated</span>
        </span>
        <span className="text-zinc-600 sm:col-span-2 lg:col-span-4">Schedule auto-syncs weekly · results auto-update only after game windows. Use the buttons for manual refresh.</span>
      </div>

      {league?.league && (
        <div data-testid="league-banner" className="rounded-xl border border-[#D50A0A]/25 bg-[#D50A0A]/5 px-4 py-3 mb-6 text-sm flex flex-wrap items-center gap-x-6 gap-y-1">
          <span className="font-display font-bold neon-text">{league.league.name}</span>
          <span className="text-zinc-400">Season: <span className="text-white">{league.league.season}</span></span>
          <span className="text-zinc-400">Starts: <span className="text-white">{league.league.start_week_label}</span></span>
          <span className="text-zinc-400">Members: <span className="text-white">{league.members}</span></span>
          <span className="text-zinc-400">Commissioner: <span className="text-white">{league.league.commissioner_email}</span></span>
        </div>
      )}

      {apiStatus && (
        <div data-testid="api-status" className={`rounded-xl border px-4 py-4 mb-6 ${apiStatus.breaker_active ? "border-red-500/40 bg-red-500/5" : "border-[#1F1F22] bg-[#0A0A0A]"}`}>
          <div className="flex items-center gap-2 mb-3">
            <Activity className="h-4 w-4 neon-text" />
            <h2 className="font-display text-sm font-bold uppercase tracking-wider">Tank01 API Status</h2>
            {apiStatus.breaker_active && (
              <span data-testid="safety-pause" className="ml-auto text-[11px] font-bold px-2 py-1 rounded bg-red-500/20 text-red-400 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Tank01 API Safety Pause Active
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
            <div>
              <div className="text-zinc-500">This month</div>
              <div data-testid="api-month" className="font-display text-lg font-bold">{apiStatus.month_used}<span className="text-zinc-600 text-sm"> / {apiStatus.month_limit}</span></div>
            </div>
            <div>
              <div className="text-zinc-500">Last 24 hours</div>
              <div data-testid="api-24h" className="font-display text-lg font-bold">{apiStatus.day_used}<span className="text-zinc-600 text-sm"> / {apiStatus.day_limit}</span></div>
            </div>
            <div>
              <div className="text-zinc-500">Last request</div>
              <div className="text-white font-medium truncate">{apiStatus.last_request ? `${apiStatus.last_request.endpoint} (${apiStatus.last_request.status})` : "—"}</div>
            </div>
            <div>
              <div className="text-zinc-500">Last trigger</div>
              <div className="text-white font-medium truncate">{apiStatus.last_request ? `${apiStatus.last_request.trigger} · ${apiStatus.last_request.mode}` : "—"}</div>
            </div>
          </div>
          {apiStatus.history?.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs text-zinc-500 cursor-pointer hover:text-white">Recent request history ({apiStatus.history.length})</summary>
              <div className="mt-2 max-h-40 overflow-y-auto space-y-1" data-testid="api-history">
                {apiStatus.history.map((h, i) => (
                  <div key={i} className="text-[11px] text-zinc-400 flex justify-between gap-3 border-b border-[#141416] py-1">
                    <span>{new Date(h.ts).toLocaleString()}</span>
                    <span className="text-zinc-300">{h.endpoint}</span>
                    <span>{h.trigger}/{h.mode}</span>
                    <span className={h.success ? "text-emerald-400" : "text-red-400"}>{String(h.status)}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      <div className="flex gap-2 mb-6">
        <button data-testid="tab-users" onClick={() => setTab("users")}
          className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors flex items-center gap-1.5 ${
            tab === "users" ? "border-[#1D4ED8] text-[#4C8DFF] bg-[#1D4ED8]/15" : "border-[#1F1F22] text-zinc-400"}`}>
          <Users className="h-4 w-4" /> Players ({users.length})
        </button>
        <button data-testid="tab-requests" onClick={() => setTab("requests")}
          className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors flex items-center gap-1.5 ${
            tab === "requests" ? "border-[#1D4ED8] text-[#4C8DFF] bg-[#1D4ED8]/15" : "border-[#1F1F22] text-zinc-400"}`}>
          <Inbox className="h-4 w-4" /> Access Requests {pendingReqs > 0 && <span className="ml-1 px-1.5 rounded-full bg-[#D50A0A] text-white text-[10px] font-bold">{pendingReqs}</span>}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 neon-text animate-spin" /></div>
      ) : tab === "users" ? (
        <div className="rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] overflow-hidden" data-testid="users-table">
          {users.length === 0 ? (
            <p className="text-zinc-500 py-16 text-center">No players yet. Add your first beta tester.</p>
          ) : users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.username}`} className="flex items-center justify-between px-5 py-4 border-b border-[#141416] last:border-0">
              <div>
                <div className="font-semibold text-sm flex items-center gap-2">
                  {u.display_name} <span className="text-zinc-500 font-normal">@{u.username}</span>
                  {u.active === false && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">INACTIVE</span>}
                  {u.submitted_this_week
                    ? <span data-testid={`submitted-${u.username}`} className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">PICKED</span>
                    : <span data-testid={`not-submitted-${u.username}`} className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-zinc-700/40 text-zinc-400">NO PICKS</span>}
                </div>
                <div className="text-xs text-zinc-600 mt-0.5">{u.picks_count} total picks</div>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`edit-user-${u.username}`} onClick={() => setEditUser(u)} title="Edit"
                  className="p-2 rounded-lg text-zinc-400 hover:text-sky-400 hover:bg-white/5 transition-colors"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`toggle-active-${u.username}`} onClick={() => toggleActive(u)} title={u.active === false ? "Activate" : "Deactivate"}
                  className={`p-2 rounded-lg hover:bg-white/5 transition-colors ${u.active === false ? "text-red-400 hover:text-emerald-400" : "text-zinc-400 hover:text-amber-400"}`}>
                  {u.active === false ? <ToggleLeft className="h-4 w-4" /> : <ToggleRight className="h-4 w-4" />}
                </button>
                <button data-testid={`reset-pw-${u.username}`} onClick={() => setResetUser(u)} title="Reset password"
                  className="p-2 rounded-lg text-zinc-400 hover:text-[#D50A0A] hover:bg-white/5 transition-colors"><KeyRound className="h-4 w-4" /></button>
                <button data-testid={`delete-user-${u.username}`} onClick={() => deleteUser(u)} title="Delete"
                  className="p-2 rounded-lg text-zinc-400 hover:text-red-400 hover:bg-white/5 transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] overflow-hidden" data-testid="requests-table">
          {requests.length === 0 ? (
            <p className="text-zinc-500 py-16 text-center">No access requests yet.</p>
          ) : requests.map((r) => (
            <div key={r.id} data-testid={`request-row-${r.id}`} className="flex items-start justify-between px-5 py-4 border-b border-[#141416] last:border-0 gap-4">
              <div>
                <div className="font-semibold text-sm">{r.name} · <span className="text-zinc-400 font-normal">{r.email}</span>
                  {r.status === "handled" && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 font-bold">HANDLED</span>}
                </div>
                {r.message && <p className="text-xs text-zinc-500 mt-1 max-w-lg">{r.message}</p>}
              </div>
              {r.status === "pending" && (
                <button data-testid={`handle-request-${r.id}`} onClick={() => markHandled(r)}
                  className="shrink-0 px-3 py-1.5 rounded-full border border-[#1F1F22] text-xs font-semibold hover:border-[#D50A0A] hover:text-[#D50A0A] transition-colors">Mark handled</button>
              )}
            </div>
          ))}
        </div>
      )}
      <EditUserDialog user={editUser} onClose={() => setEditUser(null)} onSaved={load} />
      <ResetPasswordDialog user={resetUser} onClose={() => setResetUser(null)} onDone={load} />
    </PageShell>
  );
}
